import React from "react";
import ModernDateRangePicker from "../../layouts/ModernDateRangePicker.jsx";
import ModernTimeDropdown from "../../layouts/modernDropdown.jsx";
import {
  ensureTimeSeriesRangeFilter,
  getTimeSeriesField,
  normalizeTimeSeriesConfig,
  TIME_RANGE_PRESETS,
  TIME_SERIES_DISPLAY_MODES,
  TIME_SERIES_FIELDS,
  TIME_SERIES_GRAINS,
  TIME_SERIES_TIMEZONE,
} from "./customReportVisualizationConfig.js";

const TimeSeriesSettings = ({ visualization, errors = [], onChange }) => {
  const config = normalizeTimeSeriesConfig(visualization?.timeSeries);
  const field = getTimeSeriesField(config.field) || TIME_SERIES_FIELDS[0];
  const grain = TIME_SERIES_GRAINS.find((item) => item.value === config.grain) || TIME_SERIES_GRAINS[1];
  const displayMode =
    TIME_SERIES_DISPLAY_MODES.find((item) => item.value === config.displayMode) ||
    TIME_SERIES_DISPLAY_MODES[0];
  const filters = visualization?.advancedFilters || {};
  const rangeValue = filters[field.filterKey] || "";
  const rangeError = errors.find((error) => String(error?.field || "").startsWith("timeSeries.range"));
  const displayError = errors.find(
    (error) => String(error?.field || "") === "timeSeries.displayMode"
  );

  const updateRange = (patch) => {
    onChange({
      advancedFilters: {
        ...filters,
        ...patch,
      },
    });
  };

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50/70 p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-gray-500" htmlFor={`time-series-field-${visualization?.id}`}>
            Time field
          </label>
          <ModernTimeDropdown
            id={`time-series-field-${visualization?.id}`}
            ariaLabel="Time field"
            width="w-full"
            value={field.label}
            options={TIME_SERIES_FIELDS.map((item) => item.label)}
            onChange={(event) => {
              const nextField =
                TIME_SERIES_FIELDS.find((item) => item.label === event.target.value) || TIME_SERIES_FIELDS[0];
              onChange({
                timeSeries: { ...config, field: nextField.value },
                advancedFilters: ensureTimeSeriesRangeFilter(filters, nextField.value, {
                  clearImplicitLastInteraction: true,
                  previousField: config.field,
                }),
              });
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500" htmlFor={`time-series-grain-${visualization?.id}`}>
            Group by
          </label>
          <ModernTimeDropdown
            id={`time-series-grain-${visualization?.id}`}
            ariaLabel="Time grain"
            width="w-full"
            value={grain.label}
            options={TIME_SERIES_GRAINS.map((item) => item.label)}
            onChange={(event) => {
              const nextGrain =
                TIME_SERIES_GRAINS.find((item) => item.label === event.target.value) || TIME_SERIES_GRAINS[1];
              onChange({ timeSeries: { ...config, grain: nextGrain.value } });
            }}
          />
          <p className="mt-1 text-[11px] text-gray-500">Range limit: {grain.limitLabel}.</p>
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500" htmlFor={`time-series-display-${visualization?.id}`}>
            Display as
          </label>
          <ModernTimeDropdown
            id={`time-series-display-${visualization?.id}`}
            ariaLabel="Time-series display"
            width="w-full"
            value={displayMode.label}
            options={TIME_SERIES_DISPLAY_MODES.map((item) => item.label)}
            onChange={(event) => {
              const nextMode =
                TIME_SERIES_DISPLAY_MODES.find((item) => item.label === event.target.value) ||
                TIME_SERIES_DISPLAY_MODES[0];
              onChange({ timeSeries: { ...config, displayMode: nextMode.value } });
            }}
          />
          {displayError ? (
            <p role="alert" className="mt-1 text-[11px] text-red-600">
              {displayError.message}
            </p>
          ) : null}
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500" htmlFor={`time-series-range-${visualization?.id}`}>
            {field.label} range
          </label>
          <ModernTimeDropdown
            id={`time-series-range-${visualization?.id}`}
            ariaLabel={`${field.label} range`}
            width="w-full"
            placeholder="Choose a range"
            value={rangeValue}
            options={TIME_RANGE_PRESETS}
            onChange={(event) => {
              const nextValue = event.target.value;
              updateRange({
                [field.filterKey]: nextValue,
                [field.startKey]: nextValue === "Custom Range" ? filters[field.startKey] || "" : "",
                [field.endKey]: nextValue === "Custom Range" ? filters[field.endKey] || "" : "",
                [field.rangeTypeKey]: "between",
              });
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">Timezone</label>
          <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
            {TIME_SERIES_TIMEZONE}
          </div>
        </div>
      </div>

      {rangeValue === "Custom Range" ? (
        <div className="mt-3">
          <ModernDateRangePicker
            fullWidth
            maxRangeDays={grain.maxDays - 1}
            startDate={filters[field.startKey] || ""}
            endDate={filters[field.endKey] || ""}
            rangeType="between"
            allowAfter={false}
            onChange={({ startDate, endDate }) =>
              updateRange({
                [field.startKey]: startDate,
                [field.endKey]: endDate,
                [field.rangeTypeKey]: "between",
              })
            }
          />
        </div>
      ) : null}

      {rangeError ? (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {rangeError.message}
        </p>
      ) : (
        <p className="mt-2 text-[11px] text-gray-500">
          The selected time field is also used for filtering and point drill-down.
        </p>
      )}
    </div>
  );
};

export default TimeSeriesSettings;
